<template>
  <div class="!pt-0">
    <div class="flex flex-col space-y-2.5 max-h-screen pt-16 pb-2.5">
      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mt-2.5 relative">
        <!-- Header with Status and Actions -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>

              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <h1 class="text-2xl font-bold text-gray-900">
                    {{ $t('HeadContract.ProgressClaimsShow.title') }}
                  </h1>

                  <template
                    v-if="claim"
                  >
                    <span class="text-gray-400">•</span>

                    <h2 class="text-lg font-semibold text-gray-900">
                      {{ $t('HeadContract.ProgressClaimsShow.claimNumber', { claimNumber: claim.claim_number }) }}
                    </h2>

                    <template v-if="claim?.status === ClaimStatus.certified">
                      <span class="text-gray-400">•</span>

                      <p class="text-lg text-gray-900">
                        <span class="font-medium">Amount Payable (excl. GST) {{ australianCurrency(claim.totalPayable) }}</span>
                      </p>
                    </template>

                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getStatusBadgeClass(claim.status)"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="getStatusBadgeDotClass(claim.status)"
                      />
                      {{ ClaimStatusTitles[enumKeyFromValue(ClaimStatus, claim.status)] }}
                    </span>

                    <span
                      v-if="claim.status === ClaimStatus.certified"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {{ claim.stat_dec_received ? $t('HeadContract.ProgressClaimsShow.statDecReceived') : $t('HeadContract.ProgressClaimsShow.noStatDec') }}
                    </span>
                  </template>
                </div>

                <div
                  v-if="claim"
                  class="flex items-center space-x-3"
                >
                  <p class="text-sm text-gray-600 mt-1">
                    {{ new Date(claim.start).toDateString() }} - {{ new Date(claim.end).toDateString() }}
                  </p>

                  <!-- Retention Section -->
                  <div v-if="showRetention && !isReadOnly && typingRetention">
                    <label class="flex items-center">
                      <span class="text-sm font-medium text-gray-900">{{ $t('HeadContract.ProgressClaimsShow.retention') }}</span>

                      <AppInput
                        v-model="updateForm.form.retention"
                        class="form-input ml-4 w-1/2"
                        type="text"
                        name="retention"
                        @keyup.esc="typingRetention = false"
                        @blur="updateClaim"
                        @keyup.enter="$event.target.blur()"
                      />
                    </label>
                  </div>

                  <button
                    v-else-if="showRetention && !isReadOnly"
                    type="button"
                    class="flex items-center space-x-2 cursor-pointer btn-icon btn-icon-default"
                    @click="typingRetention = true"
                  >
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('HeadContract.ProgressClaimsShow.retention') }} {{ $tcc(claim.retention) }}
                    </p>

                    <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="h-4 w-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                  </button>

                  <div v-else-if="showRetention">
                    <p class="text-sm font-medium text-gray-900">
                      {{ $t('HeadContract.ProgressClaimsShow.retention') }} {{ $tcc(claim.retention) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center space-x-3">
              <!-- Send Claim Button for Draft Status -->
              <button
                v-if="!isReadOnly && claim?.status === ClaimStatus.draft"
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click="sendClaim()"
              >
                <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                <span>{{ $t('HeadContract.ProgressClaimsShow.button.sendClaim') }}</span>
              </button>

              <!-- Action Buttons for Draft Status -->
              <div
                v-if="!isReadOnly && claim?.status === ClaimStatus.draft"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click.prevent="showImageModal = true"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.attachFiles') }}</span>
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-alert flex items-center space-x-2"
                  @click.prevent="deleteClaim"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.deleteClaim') }}</span>
                </button>
              </div>

              <!-- Action Buttons for Certified Status -->
              <div
                v-else-if="!isReadOnly && claim?.status === ClaimStatus.certified"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click.prevent="showImageModal = true"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.attachFiles') }}</span>
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  :class="{ 'opacity-50': claimExportAction.is(States.LOADING) }"
                  :disabled="claimExportAction.is(States.LOADING)"
                  @click.prevent="exportClaim"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.exportToPDF') }}</span>
                </button>
              </div>

              <!-- Resend Buttons for PQS Review Status -->
              <div
                v-else-if="!isReadOnly && claim?.status === ClaimStatus.pqs_review"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  :class="{ 'opacity-50': resendPqsAction.is(States.LOADING) }"
                  :disabled="resendPqsAction.is(States.LOADING)"
                  @click.prevent="resendPqsEmail"
                >
                  <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.resendPqs') }}</span>
                </button>
              </div>

              <!-- Resend Buttons for Contractor Claim Status -->
              <div
                v-else-if="!isReadOnly && claim?.status === ClaimStatus.contractor_claim"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  :class="{ 'opacity-50': resendContractorAction.is(States.LOADING) }"
                  :disabled="resendContractorAction.is(States.LOADING)"
                  @click.prevent="resendContractorEmail"
                >
                  <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.resendContractor') }}</span>
                </button>
              </div>

              <!-- Resend Button for Draft PQS Status -->
              <div
                v-else-if="!isReadOnly && claim?.status === ClaimStatus.draft_pqs"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  :class="{ 'opacity-50': resendPqsAction.is(States.LOADING) }"
                  :disabled="resendPqsAction.is(States.LOADING)"
                  @click.prevent="resendPqsEmail"
                >
                  <svg

    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
 class="w-4 h-4">
    <path
    stroke-linecap="round"
    stroke-linejoin="round"
    stroke-width="2"
    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
</svg>

                  <span>{{ $t('HeadContract.ProgressClaimsShow.button.resendPqs') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <ClaimTable
          :project-id="projectId"
          :claim-id="claimId"
        />
      </div>
    </div>

    <CreateClaimModal
      v-if="show"
      :project-id="$props.projectId"
      boq-type="direct"
      @close="show = false"
    />

    <ClaimAttachmentsModal
      v-if="showImageModal"
      :claim-id="claimId"
      @close="showImageModal = false;"
    />
  </div>
</template>

<script setup lang="ts">
import ClaimAttachmentsModal from '@/components/HeadContract/ProgressClaimsLite/ClaimAttachmentsModal.vue';
import ClaimTable from '@/components/ProgressClaims/ClaimTable.vue';
import CreateClaimModal from '@/components/ProgressClaims/CreateClaimModal.vue';
import { promptCertificateLayout } from '@/composables/useCertificateLayoutSwal';
import { useFormData } from '@/composables/useFormData';
import { useRequestClaimApproval } from '@/composables/useRequestClaimApproval';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, enumKeyFromValue, falsey } from '@/helpers';
import { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { useUserStore } from '@/store/user';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  claimId?: string;
}>();

const claimsStore = useClaimsStore();
const router = useRouter();
const projectsStore = useProjectsStore();
const sharedFundsStore = useSharedFundsStore();
const userStore = useUserStore();
const isReadOnly = userStore.isClientManager();
const { requestApproval } = useRequestClaimApproval();

if(projectsStore.project?.project_group_id) {
  sharedFundsStore.fetchClaimIndex(props.projectId).catch(console.error);
}

watch(() => [props.projectId, props.claimId], () => {
  if(props.claimId) {
    claimsStore.fetchClaim(props.claimId);
  } else {
    claimsStore.fetchClaims(props.projectId, { boq_type: 'direct' });
  }
}, { immediate: true });

const claim = computed(() => {
  // TODO: 10/11/23 constrain variation orders to only those with cost for the current claim
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items');
      })
      .with('trade_item_costs')
      .with('variation_orders')
      .with('variation_order_costs')
      .with('shared_fund_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const show = ref(false);
const showImageModal = ref(false);

const sendClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Send Claim To',
    input: 'select',
    inputOptions: {
      // [ClaimStatus.draft_contractor]: 'Contractor',
      [ClaimStatus.draft_pqs]: 'PQS Firm',
    },

    inputPlaceholder: 'Select a Contact',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      claimsStore.sendClaim(claim.value.id, { status: result.value });
    }
  });
};

const claims = computed(() => {
  return claimsStore.models.where('project_id', parseInt(props.projectId)).where('boq_type', 'direct').orderBy(
    'claim_number',
    'desc',
  ).orderBy(
    'claim_number',
    'desc',
  ).get();
});

const latestClaim = computed(() => {
  if(claims.value.length) {
    return claims.value[0];
  }

  return undefined;
});

const updateForm = useFormData({
  retention: 0,
});

const typingRetention = ref(false);

watch(claim, (newValue) => {
  if(newValue) {
    updateForm.setData(newValue);
  }
});

const updateClaimAction = useStoreApiAction(claimsStore.updateClaim);

const updateClaim = () => {
  if(!typingRetention.value) {
    updateForm.form.retention = claim.value.retention;

    return;
  }

  if(falsey(updateForm.form.retention)) {
    typingRetention.value = false;

    updateForm.form.retention = claim.value.retention;

    return;
  }

  updateClaimAction.request(claim.value.id, updateForm.form).then(() => {
    typingRetention.value = false;
  }).catch((error) => {
    Swal.fire({
      icon: 'error',
      title: 'Failed to update retention',
      text: error.data?.message,
    });

    typingRetention.value = true;
  });
};

const deleteClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Claim?',
    text: 'Are you sure you want to delete this claim? This cannot be undone.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      claimsStore.deleteClaim(claim.value.id).then(() => {
        router.push({ name: 'ProgressClaimsIndex' });

        Swal.fire({
          icon: 'success',
          title: 'Claim deleted.',
        });
      });
    }
  });
};

const claimExportAction = useStoreApiAction(claimsStore.export);

const exportClaim = async () => {
  const compact = await promptCertificateLayout();

  if(compact === undefined) {
    return;
  }

  claimExportAction.request(claim.value.id, { compact }).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Claim Exported.',
      text: 'Claim is being exported to PDF and will be sent to your email when it is ready.',
    });
  }).catch((error) => {
    console.trace(error);

    if(error?.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'PDF is currently being exported',
        text: error.data.message,
      });
    }
  });
};

const resendPqsAction = useStoreApiAction(claimsStore.resendPqs);

const resendPqsEmail = () => {
  resendPqsAction.request(claim.value.id).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Email Sent.',
      text: 'Email has been resent to PQS successfully.',
    });
  }).catch((error) => {
    console.trace(error);

    if(error?.status !== 500) {
      Swal.fire({
        icon: 'error',
        title: 'Email is currently being resent',
        text: error.data.message,
      });
    }
  });
};

const resendContractorAction = useStoreApiAction(claimsStore.resendContractor);

const resendContractorEmail = () => {
  resendContractorAction.request(claim.value.id).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Email Sent.',
      text: 'Contractor email has been resent successfully.',
    });
  }).catch((error) => {
    console.trace(error);

    if(error?.status !== 500) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to resend email',
        text: error.data?.message || 'An error occurred while resending the email.',
      });
    }
  });
};

// Status badge helper functions
const getStatusBadgeClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-100 text-green-800';

    case ClaimStatus.pqs_review:
      return 'bg-yellow-100 text-yellow-800';

    case ClaimStatus.contractor_claim:
      return 'bg-blue-100 text-blue-800';

    case ClaimStatus.draft_pqs:
      return 'bg-gray-100 text-gray-800';

    default:
      return 'bg-red-100 text-red-800';
  }
};

const getStatusBadgeDotClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-400';

    case ClaimStatus.pqs_review:
      return 'bg-yellow-400';

    case ClaimStatus.contractor_claim:
      return 'bg-blue-400';

    case ClaimStatus.draft_pqs:
      return 'bg-gray-400';

    default:
      return 'bg-red-400';
  }
};

const showRetention = computed(() => {
  return projectsStore.project?.head_contract_settings?.performance_security === 'retention';
});
</script>

<style scoped></style>
